import React, { FC, PropsWithChildren, useCallback } from 'react';
import styled from 'styled-components';

import { Button, Icon, ToggleHeight, Tooltip } from '@column/column-ui-kit';

interface SectionProps extends PropsWithChildren {
  title: string;
  isOpen: boolean;
  isClearable: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onFilterClear?: () => void;
}

const Wrapper = styled.div`
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
`;

const Title = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 20px;
  font-size: 14px;
  font-weight: 500;
  padding: 12px var(--page-margin);
  color: ${({ theme }) => theme.foreground};
`;

const Arrow = styled(Icon.ChevronDown)<{ isOpen: boolean }>`
  --icon-size: 20px;

  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: transform 0.2s;
  transform: scaleY(${({ isOpen }) => (isOpen ? -1 : 1)});
`;

const Content = styled.div`
  padding: 0 var(--page-margin) 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClearTooltip = styled(Tooltip)<{ $isVisible: boolean }>`
  transition:
    opacity 0.2s,
    transform 0.2s;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transform: translateX(${({ $isVisible }) => ($isVisible ? '0' : '-4px')});
`;

const ClearButton = styled(Button)`
  svg {
    --icon-size: 20px;
  }
`;

const Label = styled.div``;

export const Section: FC<SectionProps> = ({ children, title, isOpen, isClearable, onOpenChange, onFilterClear }) => {
  const handleTitleClick = useCallback(() => {
    onOpenChange?.(!isOpen);
  }, [isOpen, onOpenChange]);

  const handleFilterClick = useCallback(() => {
    onOpenChange?.(false);
    onFilterClear?.();
  }, [onOpenChange, onFilterClear]);

  return (
    <Wrapper>
      <Title onClick={handleTitleClick}>
        <Label>{title}</Label>

        <Toolbar>
          <ClearTooltip content="Clear filters" placement="top" zIndex={15} $isVisible={!!onFilterClear && isClearable}>
            <ClearButton
              variant="subtle"
              size="small"
              onClick={handleFilterClick}
              icon={<Icon.Reset />}
              type="button"
            />
          </ClearTooltip>

          <Arrow isOpen={isOpen} />
        </Toolbar>
      </Title>

      <ToggleHeight isClose={!isOpen}>
        <Content>{children}</Content>
      </ToggleHeight>
    </Wrapper>
  );
};
